/**
 * First-party event tracking (spec section 16).
 *
 * The event *names* are the contract, and they are fixed by the spec. The
 * transport is not: this module owns the seam and a development-only default,
 * and `lib/analyticsTransport.ts` installs the real one — a beacon to
 * `POST /api/events` — by calling `setAnalyticsTransport` once at bootstrap.
 * Nothing that calls `track` knows or cares which is installed.
 *
 * First-party events are the authoritative record (spec section 16). Plausible,
 * when it is configured, gets pageviews and nothing else: no custom events are
 * sent to it, so there is only ever one system to reconcile a number against.
 *
 * Two rules for callers:
 *
 * - **Never pass personal data.** Event properties describe the interaction —
 *   which form, which audience, which field failed — never what was typed
 *   into it (spec sections 14 and 19).
 * - **Fire once.** The forms are reusable components and a page may render
 *   more than one, so `form_started` is guarded per form instance and the
 *   completion events fire on the one submission that succeeded.
 */

/** The tracked event names from spec section 16. This union is the allow list. */
export type AnalyticsEventName =
  | 'audience_selected'
  | 'cta_clicked'
  | 'form_started'
  | 'form_validation_error'
  | 'owner_signup_completed'
  | 'provider_signup_completed'
  | 'referral_started'
  | 'referral_completed'
  | 'share_link_copied';

/** Scalar properties only. `undefined` entries are dropped before transport. */
export type AnalyticsProps = Record<string, string | number | boolean | undefined>;

export type AnalyticsTransport = (event: AnalyticsEventName, props?: AnalyticsProps) => void;

/**
 * The default transport: prints the event in development so a placement can be
 * checked in a browser console, and does nothing otherwise. It is what runs
 * before bootstrap installs the real one, and what tests get unless they
 * install their own.
 */
const devTransport: AnalyticsTransport = (event, props) => {
  if (!import.meta.env.DEV) return;
  console.info(`[analytics] ${event}`, props ?? {});
};

let transport: AnalyticsTransport = devTransport;

/** Installs the real transport. Called once, from bootstrap (main.tsx). */
export function setAnalyticsTransport(next: AnalyticsTransport): void {
  transport = next;
}

/** Restores the built-in no-op transport. Used by tests. */
export function resetAnalyticsTransport(): void {
  transport = devTransport;
}

function compact(props: AnalyticsProps | undefined): AnalyticsProps | undefined {
  if (!props) return undefined;
  const entries = Object.entries(props).filter(([, value]) => value !== undefined);
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

/**
 * Records an event. Never throws: a failing analytics transport must not be
 * able to break a signup, which is the only thing on this site that matters.
 */
export function track(event: AnalyticsEventName, props?: AnalyticsProps): void {
  try {
    transport(event, compact(props));
  } catch {
    // Analytics is best effort by design.
  }
}
